import { useRetailerCoreWithSelector } from "@faire/retailer-visitor-shared/hooks/retailer/RetailerCoreProvider";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { CurrencyUnit } from "@faire/web-api--source/faire/currency/CurrencyUnit";
import { ISO3166Alpha3 } from "@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3";
import { THREE_LETTER_EUROPE_SUPPORTED_COUNTRIES } from "@faire/web/common/consts/THREE_LETTER_EUROPE_SUPPORTED_COUNTRIES";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { createContext, useContext, useMemo } from "react";
import { createStore, StoreApi, useStore } from "zustand";

interface RetailerNationality {
  retailerCountry: ISO3166Alpha3 | undefined;
  isAmerican: boolean;
  isCanadian: boolean;
  isUK: boolean;
  isAustralian: boolean;
  isEurope: boolean;
  isEuropeNonUk: boolean;
  isNewOrMigratedCanadian: boolean;
  isInternational: boolean;
  geographicState: string | undefined;
}

const RetailerNationalityContext = createContext<
  StoreApi<RetailerNationality> | undefined
>(undefined);

export const RetailerNationalityProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const initialRetailer = getRetailer();
  const retailer = useRetailerCoreWithSelector((state) => state.retailer);
  const store = useMemo(
    () =>
      createStore<RetailerNationality>(() => {
        const retailerCountry = (retailer?.country ??
          initialRetailer?.country ??
          getSessionCountry()) as ISO3166Alpha3;
        const isAmerican =
          retailerCountry === THREE_LETTER_PRIMARY_COUNTRY_CODES.USA;
        const isCanadian =
          retailerCountry === THREE_LETTER_PRIMARY_COUNTRY_CODES.CAN;
        const isUK = retailerCountry === THREE_LETTER_PRIMARY_COUNTRY_CODES.GBR;
        const isAustralian =
          retailerCountry === THREE_LETTER_PRIMARY_COUNTRY_CODES.AUS;
        const isEurope = THREE_LETTER_EUROPE_SUPPORTED_COUNTRIES.includes(
          retailerCountry as (typeof THREE_LETTER_EUROPE_SUPPORTED_COUNTRIES)[number]
        );
        const isEuropeNonUk = isEurope && !isUK;
        const isNewOrMigratedCanadian =
          isCanadian &&
          (retailer?.currency ?? initialRetailer?.currency) ===
            CurrencyUnit.CAD;
        const isInternational = !isAmerican && !isCanadian;

        return {
          retailerCountry,
          isAmerican,
          isCanadian,
          isUK,
          isAustralian,
          isEurope,
          isEuropeNonUk,
          isNewOrMigratedCanadian,
          isInternational,
          geographicState:
            retailer?.address?.state ?? initialRetailer?.address?.state,
        };
      }),
    [
      retailer?.country,
      retailer?.currency,
      retailer?.address?.state,
      initialRetailer?.country,
      initialRetailer?.currency,
      initialRetailer?.address?.state,
    ]
  );

  return (
    <RetailerNationalityContext.Provider value={store}>
      {children}
    </RetailerNationalityContext.Provider>
  );
};

export const useNationality = () => {
  const context = useContext(RetailerNationalityContext);
  if (!context) {
    throw new Error(
      "RetailerNationalityContext not found. Must be used within RetailerNationalityProvider"
    );
  }
  return useStore(context);
};
